import './agTopology.scss';

import HealthBadge from './HealthBadge';

import AvailabilityGroup from '../../types/cluster/AvailabilityGroup';
import AvailabilityReplica from '../../types/cluster/AvailabilityReplica';
import AvailabilityListener from '../../types/cluster/AvailabilityListener';

interface AgTopologyProps {
    group: AvailabilityGroup,
    replicas: AvailabilityReplica[],
    listener?: AvailabilityListener
}

// Unknown means no recent data, which is not the same as a fault.
const stateClass = (good: boolean, unknown: boolean, warning = false) =>
    good ? 'healthy' : unknown ? 'neutral' : warning ? 'warning' : 'critical';

const prettyMode = (mode: string) => mode === 'SYNCHRONOUS_COMMIT' ? 'Synchronous commit' : 'Asynchronous commit';

function ReplicaCard(props: { replica: AvailabilityReplica }) {
    const { replica } = props;
    const cardState = stateClass(replica.synchronizationhealth === 'HEALTHY',
        replica.synchronizationhealth === 'UNKNOWN', replica.synchronizationhealth === 'PARTIALLY_HEALTHY');

    return (
        <div className={`ag-replica-card ${cardState}`}>
            <div className="replica-header">
                <i className={`fal fa-fw ${replica.role === 'PRIMARY' ? 'fa-crown' : 'fa-clone'}`} aria-hidden="true"/>
                <span className="replica-name">{replica.servername}</span>
                <HealthBadge value={replica.synchronizationhealth}/>
            </div>
            <div className="replica-body">
                <div>{replica.role === 'PRIMARY' ? 'Primary replica' : 'Secondary replica'}</div>
                <div>{prettyMode(replica.availabilitymode)}</div>
                <div>{replica.failovermode === 'AUTOMATIC' ? 'Automatic failover' : 'Manual failover'}</div>
                {replica.connectedstate === 'UNKNOWN'
                    ? <div>Connection unknown</div>
                    : <div className={replica.connectedstate === 'CONNECTED' ? '' : 'text-danger'}>
                        {replica.connectedstate === 'CONNECTED' ? 'Connected' : 'Disconnected'}
                    </div>}
            </div>
        </div>
    );
}

function AgTopology(props: AgTopologyProps) {
    const { group, replicas, listener } = props;

    const primary = replicas.find(replica => replica.role === 'PRIMARY');
    const secondaries = replicas.filter(replica => replica.role !== 'PRIMARY');

    return (
        <div className="ag-topology">
            {listener &&
                <div className={`ag-listener ${stateClass(listener.state === 'ONLINE', listener.state === 'UNKNOWN')}`}>
                    <i className="fal fa-router fa-fw" aria-hidden="true"/>
                    <span className="fw-bold">{listener.dnsname}</span>
                    <span className="text-muted">{listener.ipaddresses.join(', ')}:{listener.port}</span>
                    <HealthBadge value={listener.state}/>
                </div>}
            <div className="ag-replicas">
                {primary && <ReplicaCard replica={primary}/>}
                <div className="ag-arrows">
                    {secondaries.map(replica => (
                        <div key={replica.id}
                             className={`ag-arrow ${replica.connectedstate === 'CONNECTED'
                                 ? '' : replica.connectedstate === 'UNKNOWN' ? 'unknown' : 'broken'}`}
                             data-tooltip-id="global-tooltip"
                             data-tooltip-content={replica.connectedstate === 'CONNECTED'
                                 ? `Log blocks flowing to ${replica.servername}`
                                 : replica.connectedstate === 'UNKNOWN'
                                     ? `No recent data for ${replica.servername}`
                                     : `${replica.servername} is disconnected — data movement stopped`}>
                            <span className="arrow-line"/>
                            <i className={`fal fa-fw ${replica.connectedstate === 'CONNECTED'
                                ? 'fa-angle-double-right' : replica.connectedstate === 'UNKNOWN' ? 'fa-question' : 'fa-times'}`}
                               aria-hidden="true"/>
                        </div>
                    ))}
                </div>
                <div className="ag-secondaries">
                    {secondaries.map(replica => <ReplicaCard key={replica.id} replica={replica}/>)}
                </div>
            </div>
            {group.failoverready === false &&
                <div className="ag-warning-strip">
                    <i className="fal fa-exclamation-triangle fa-fw amber-icon" aria-hidden="true"/>
                    This availability group is not ready for automatic failover.
                </div>}
        </div>
    );
}

export default AgTopology;
